import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiStar, FiSend } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useReviewPrompt } from '../contexts/ReviewPromptContext';
import {
  recordPromptOutcome,
  requestNativeReview,
} from '../services/reviewPromptService';
import { createSupportTicket } from '../services/supportService';
import { useToast } from '../contexts/ToastContext';

// Two-step modal: "Are you enjoying TeeSync?" → Yes calls SKStoreReviewController,
// No surfaces an inline feedback textarea that creates a support_tickets row.
// Whichever path the user takes (or dismiss), we persist the outcome to
// gate re-prompts for 90 days.
function ReviewPromptModal() {
  const { isOpen, close } = useReviewPrompt();
  const toast = useToast();
  const [step, setStep] = useState('ask'); // 'ask' | 'feedback'
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleClose = async () => {
    // If the user dismisses without committing to yes/no, persist a soft
    // outcome so we don't ask again for 90 days.
    await recordPromptOutcome('dismissed');
    setStep('ask');
    setFeedback('');
    close();
  };

  const handleYes = async () => {
    try {
      await requestNativeReview();
      await recordPromptOutcome('yes');
      toast.success('Thanks for the support!');
    } catch (err) {
      console.error('[ReviewPromptModal] native review failed:', err);
      toast.info('Could not open the review sheet. Please try again later.');
      await recordPromptOutcome('dismissed');
    } finally {
      setStep('ask');
      setFeedback('');
      close();
    }
  };

  const handleNo = () => {
    setStep('feedback');
  };

  const handleSubmitFeedback = async () => {
    if (!feedback.trim()) return;
    setSubmitting(true);
    try {
      await createSupportTicket({
        // 'other' is the closest existing category to "general improvement
        // suggestion" — the support_tickets check constraint only allows
        // bug | feature | question | other.
        category: 'other',
        subject: 'In-app feedback',
        message: feedback.trim(),
      });
      await recordPromptOutcome('no');
      toast.success("Thanks — we'll take a look.");
      setStep('ask');
      setFeedback('');
      close();
    } catch (err) {
      console.error('[ReviewPromptModal] feedback submit failed:', err?.message || err, err);
      toast.info(err?.message || 'Could not send feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            className="bg-card rounded-2xl shadow-xl border border-border-subtle w-full max-w-sm p-6 relative"
          >
            <button
              type="button"
              onClick={handleClose}
              className="absolute top-3 right-3 text-text-muted hover:text-text-primary"
              aria-label="Close"
            >
              <SafeIcon icon={FiX} className="w-4 h-4" />
            </button>

            {step === 'ask' && (
              <>
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-3">
                  <SafeIcon icon={FiStar} className="w-6 h-6 text-accent" />
                </div>
                <h2 className="text-lg font-bold text-text-primary mb-1">
                  Are you enjoying TeeSync?
                </h2>
                <p className="text-sm text-text-secondary mb-5">
                  We'd love to hear how it's going for you.
                </p>
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={handleYes}
                    className="w-full px-4 py-3 bg-accent text-white rounded-md hover:bg-interactive-hover transition-colors font-semibold min-h-[44px]"
                  >
                    Yes, I'm enjoying it
                  </button>
                  <button
                    type="button"
                    onClick={handleNo}
                    className="w-full px-4 py-3 bg-card border border-border-subtle text-text-primary rounded-md hover:border-accent transition-colors font-medium min-h-[44px]"
                  >
                    Not really
                  </button>
                </div>
              </>
            )}

            {step === 'feedback' && (
              <>
                <h2 className="text-lg font-bold text-text-primary mb-1">
                  Tell us what we can improve
                </h2>
                <p className="text-sm text-text-secondary mb-3">
                  We'll read every message — share what's not working for you.
                </p>
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="What would make TeeSync better?"
                  rows={4}
                  className="w-full px-3 py-2 border border-border-subtle rounded-md focus:ring-1 focus:ring-accent focus:border-accent bg-surface text-text-primary placeholder-text-muted text-sm mb-4"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setStep('ask')}
                    className="flex-1 px-4 py-3 bg-card border border-border-subtle text-text-primary rounded-md hover:border-accent transition-colors font-medium min-h-[44px]"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmitFeedback}
                    disabled={!feedback.trim() || submitting}
                    className="flex-1 px-4 py-3 bg-accent text-white rounded-md hover:bg-interactive-hover transition-colors font-semibold disabled:opacity-50 min-h-[44px] inline-flex items-center justify-center gap-2"
                  >
                    <SafeIcon icon={FiSend} className="w-4 h-4" />
                    {submitting ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default ReviewPromptModal;
